import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Lock, Users, Heart, MessageCircle, Flag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Poster from "./Poster";
import StarRating from "./StarRating";
import CommentThread from "./CommentThread";
import ReportModal from "./ReportModal";
import { likeLog, unlikeLog, deleteLog as apiDeleteLog } from "../lib/services";
import { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { lower } from "../lib/strings";

const visibilityIcon = (v) => {
  if (v === "private") return <Lock className="w-3 h-3 inline" strokeWidth={2} />;
  if (v === "followers") return <Users className="w-3 h-3 inline" strokeWidth={2} />;
  return null;
};

const visibilityLabel = (v) => {
  if (v === "private") return "private";
  if (v === "followers") return "followers";
  return null;
};

// canonical log card. used in: profile diary, show activity, home feeds, list detail.
// hardened to tolerate any shape of `log`, `show`, `user` — including completely
// missing ones — so a malformed item in any feed cannot crash a parent route.
export default function LogCard({
  log = {},
  show,
  user,
  showUser = false,
  className = "",
  onDeleted,
}) {
  const { user: me } = useAuth();
  const [revealed, setRevealed] = useState(false);
  const [liked, setLiked] = useState(!!log.likedByMe);
  const [likeCount, setLikeCount] = useState(log.likeCount || 0);
  const [commentCount, setCommentCount] = useState(log.commentCount || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const noteHidden = log.hasSpoilers && !revealed;
  const noteText = log.note && log.note !== "(no note)" ? log.note : null;
  const visLabel = visibilityLabel(log.visibility);
  const dateText = log.dateSeen || log.date || "today";
  const ratingValue = log.rating ?? log.stars ?? null;
  const isMine = me?.id && log.userId && me.id === log.userId;
  const canDelete = log.id && (isMine || me?.role === "admin");
  const adminOverride = me?.role === "admin" && !isMine;

  // safe fallbacks for missing nested data
  const showId = show?.id || log.showId || null;
  const showTitle = show?.title || log.showId || "unknown show";
  const linkTo = showId ? `/show/${showId}` : "#";
  const posterShow = show || { id: showId || "unknown", title: showTitle, theatre: "" };

  const onConfirmDelete = async () => {
    if (!log.id) return;
    setDeleting(true);
    try {
      await apiDeleteLog(log.id);
      toast(adminOverride ? "log removed (admin)" : "log deleted");
      setConfirmingDelete(false);
      onDeleted?.(log.id);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const onToggleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      const res = next ? await likeLog(log.id) : await unlikeLog(log.id);
      if (typeof res?.likeCount === "number") setLikeCount(res.likeCount);
    } catch (err) {
      // revert on failure
      console.warn("like toggle failed:", err);
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
    }
  };

  return (
    <article
      data-testid={`log-card-${log.id || "anon"}`}
      className={`flex gap-4 ${className}`}
    >
      <Link to={linkTo} className="shrink-0">
        <Poster show={posterShow} size="md" />
      </Link>

      <div className="flex-1 min-w-0">
        {showUser && user && (
          <Link
            to={`/user/${user.username || ""}`}
            data-testid={`log-user-${log.id}`}
            className="font-mono text-[10px] uppercase tracking-widest text-acid hover:underline"
          >
            {lower(user.displayName) || lower(user.username) || "user"}
          </Link>
        )}

        <Link to={linkTo}>
          <h3 className="font-display text-xl leading-tight hover:text-acid transition-colors break-words">
            {lower(showTitle)}
          </h3>
        </Link>

        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1 flex items-center gap-1.5 flex-wrap">
          <span>{dateText}</span>
          {visLabel && (
            <span className="text-white/40 inline-flex items-center gap-1">
              · {visibilityIcon(log.visibility)} {visLabel}
            </span>
          )}
          {log.hasSpoilers && (
            <span className="text-acid inline-flex items-center gap-1">
              · spoilers
            </span>
          )}
        </div>

        {ratingValue ? (
          <div className="mt-1.5">
            <StarRating
              value={ratingValue}
              readOnly
              size={16}
              testIdPrefix={`log-stars-${log.id}`}
            />
          </div>
        ) : null}

        {noteText && (
          noteHidden ? (
            <button
              data-testid={`spoiler-reveal-${log.id}`}
              onClick={() => setRevealed(true)}
              className="mt-2 w-full text-left font-mono text-xs text-white/60 border-2 border-white/20 px-3 py-2 hover:border-acid hover:text-acid transition-colors flex items-center gap-1.5"
            >
              <Eye className="w-3 h-3" strokeWidth={2} />
              spoiler hidden — tap to reveal
            </button>
          ) : (
            <p
              data-testid={`log-note-${log.id}`}
              className="mt-2 font-display text-base text-white/90 leading-snug"
            >
              "{noteText}"
            </p>
          )
        )}

        {/* engagement row — only show when we actually have a real log id */}
        {log.id && (
        <div
          data-testid={`log-actions-${log.id}`}
          className="mt-3 flex items-center gap-4 font-mono text-[11px] text-white/50"
        >
          <button
            data-testid={`like-btn-${log.id}`}
            onClick={onToggleLike}
            className={`inline-flex items-center gap-1 transition-colors ${
              liked ? "text-acid" : "hover:text-acid"
            }`}
            aria-label={liked ? "unlike" : "like"}
          >
            <Heart
              className="w-3.5 h-3.5"
              strokeWidth={2}
              fill={liked ? "currentColor" : "none"}
            />
            <span data-testid={`like-count-${log.id}`} className="tabular-nums">
              {likeCount}
            </span>
          </button>
          <button
            data-testid={`comment-btn-${log.id}`}
            onClick={() => setCommentsOpen((v) => !v)}
            className="inline-flex items-center gap-1 hover:text-acid transition-colors"
            aria-label="comments"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
            <span data-testid={`comment-count-${log.id}`} className="tabular-nums">
              {commentCount}
            </span>
          </button>
          {!isMine && (
            <button
              data-testid={`report-log-${log.id}`}
              onClick={() => setReporting(true)}
              className="ml-auto inline-flex items-center gap-1 text-white/30 hover:text-white/80 transition-colors"
              aria-label="report this log"
            >
              <Flag className="w-3 h-3" strokeWidth={2} />
              report
            </button>
          )}
          {canDelete && (
            <button
              data-testid={`delete-log-${log.id}`}
              onClick={() => setConfirmingDelete(true)}
              className={`${isMine ? "ml-auto" : ""} inline-flex items-center gap-1 text-white/30 hover:text-acid transition-colors`}
              aria-label={adminOverride ? "remove this log (admin)" : "delete this log"}
            >
              <Trash2 className="w-3 h-3" strokeWidth={2} />
              {adminOverride ? "remove" : "delete"}
            </button>
          )}
        </div>
        )}

        {confirmingDelete && (
          <div
            data-testid={`delete-confirm-${log.id}`}
            className="mt-3 border-2 border-acid bg-acid/5 p-3 font-mono text-xs"
          >
            <p className="text-acid">
              {adminOverride ? "remove this log as admin?" : "delete this log?"}
            </p>
            <p className="text-white/50 mt-1 text-[11px] leading-relaxed">
              this is permanent. likes and comments will be removed too.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                data-testid={`delete-confirm-yes-${log.id}`}
                onClick={onConfirmDelete}
                disabled={deleting}
                className="font-mono text-[11px] uppercase tracking-wider bg-acid text-black px-3 py-1.5 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-40"
              >
                {deleting ? "deleting..." : (adminOverride ? "remove" : "delete")}
              </button>
              <button
                data-testid={`delete-confirm-no-${log.id}`}
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="font-mono text-[11px] uppercase tracking-wider border-2 border-white/30 text-white/60 px-3 py-1.5 hover:border-white hover:text-white transition-colors disabled:opacity-40"
              >
                cancel
              </button>
            </div>
          </div>
        )}

        {log.id && (
          <CommentThread
            logId={log.id}
            open={commentsOpen}
            onCountChange={setCommentCount}
          />
        )}

        {log.id && (
          <ReportModal
            open={reporting}
            onClose={() => setReporting(false)}
            targetType="log"
            targetId={log.id}
            label={showTitle ? `log of ${lower(showTitle)}` : "log"}
          />
        )}
      </div>
    </article>
  );
}
